import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { calculateRegistrationPrice } from 'src/helpers/registrationPrice';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { WizardRegisterDraft, canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { IEdition } from 'src/interfaces/Edition';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IBaseTechnicalType } from 'src/interfaces/TechnicalType';

interface Props {
  activeEdition?: IEdition;
  unregisteredParticipants: IEnrichedParticipant[];
  registerDrafts: Record<string, WizardRegisterDraft>;
  onUpdateDraft: (participantId: string, patch: Partial<WizardRegisterDraft>) => void;
  competitionsById?: Record<string, IBaseTechnicalType>;
  distancesById?: Record<string, IBaseTechnicalType>;
  onRegisterAll: () => void;
  isRegistering: boolean;
  needsRegistration: boolean;
}

export default function RegisterStep({
  activeEdition,
  unregisteredParticipants,
  registerDrafts,
  onUpdateDraft,
  competitionsById,
  distancesById,
  onRegisterAll,
  isRegistering,
  needsRegistration,
}: Props) {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const editionCompetitions = activeEdition?.competitions ?? [];

  const handleCompetitionChange = (participant: IEnrichedParticipant, competitionId: string) => {
    const comp = editionCompetitions.find(c => c.competitionId === competitionId);
    const distanceId = sortDistanceIds(comp?.distances ?? [], distancesById)[0] ?? '';
    const price =
      activeEdition && distanceId
        ? calculateRegistrationPrice(
            activeEdition,
            {
              dateOfBirth: participant.dateOfBirth,
              age: participant.age,
              discounted: participant.discounted,
            },
            { isAdminContext: true },
          )
        : 0;
    onUpdateDraft(participant.id, { competitionId, distanceId, price });
  };

  const handleDistanceChange = (participant: IEnrichedParticipant, distanceId: string) => {
    const draft = registerDrafts[participant.id];
    const price =
      activeEdition && draft?.competitionId
        ? calculateRegistrationPrice(
            activeEdition,
            {
              dateOfBirth: participant.dateOfBirth,
              age: participant.age,
              discounted: participant.discounted,
            },
            { isAdminContext: true },
          )
        : 0;
    onUpdateDraft(participant.id, { distanceId, price });
  };

  if (!needsRegistration) {
    return (
      <Typography color="text.secondary">{t('userCheckinWizard.noRegistrationNeeded')}</Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {t('userCheckinWizard.registerSectionTitle')}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('form.name')}</TableCell>
              <TableCell>{t('drawer.competitions')}</TableCell>
              <TableCell>{t('drawer.distances')}</TableCell>
              <TableCell>{t('export.price')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unregisteredParticipants.map(p => {
              const draft = registerDrafts[p.id] ?? {
                competitionId: '',
                distanceId: '',
                price: 0,
              };
              const canRegister = canRegisterParticipant(p);
              const distancesForComp = sortDistanceIds(
                editionCompetitions.find(c => c.competitionId === draft.competitionId)?.distances ??
                  [],
                distancesById,
              );

              return (
                <TableRow key={p.id}>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2">
                        {p.lastName} {p.firstName}
                      </Typography>
                      {!canRegister ? (
                        <Typography variant="caption" color="error" component="p" sx={{ m: 0 }}>
                          {t('userCheckinWizard.missingDateOfBirth')}
                        </Typography>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small" disabled={!canRegister}>
                      <InputLabel>{t('drawer.competitions')}</InputLabel>
                      <Select
                        value={draft.competitionId}
                        label={t('drawer.competitions')}
                        onChange={e => handleCompetitionChange(p, e.target.value)}
                      >
                        {editionCompetitions.map(comp => {
                          const competition = competitionsById?.[comp.competitionId];
                          return competition ? (
                            <MenuItem key={comp.competitionId} value={comp.competitionId}>
                              {competition.i18n[currentLang]}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!canRegister || !draft.competitionId}
                    >
                      <InputLabel>{t('drawer.distances')}</InputLabel>
                      <Select
                        value={draft.distanceId}
                        label={t('drawer.distances')}
                        onChange={e => handleDistanceChange(p, e.target.value)}
                      >
                        {distancesForComp.map(distanceId => {
                          const distance = distancesById?.[distanceId];
                          return distance ? (
                            <MenuItem key={distanceId} value={distanceId}>
                              {distance.i18n[currentLang]}
                            </MenuItem>
                          ) : null;
                        })}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TextField
                        size="small"
                        type="number"
                        disabled={!canRegister}
                        value={draft.price}
                        onChange={e => onUpdateDraft(p.id, { price: Number(e.target.value) || 0 })}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                            ),
                          },
                        }}
                      />
                      {typeof activeEdition?.groupPrice === 'number' &&
                      !Number.isNaN(activeEdition.groupPrice) ? (
                        <Button
                          size="small"
                          disabled={!canRegister}
                          onClick={() =>
                            onUpdateDraft(p.id, { price: activeEdition.groupPrice as number })
                          }
                        >
                          {t('checkins.applyGroupPrice')}
                        </Button>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={onRegisterAll} disabled={isRegistering}>
          {t('userCheckinWizard.registerAll')}
        </Button>
      </Box>
    </Box>
  );
}
